"use client";
import React, { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  ArrowLeft,
  HelpCircle,
  ChevronDown,
  Package,
  Truck,
  CreditCard,
  Sparkles,
  Globe,
  MessageCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import Link from "next/link";
import { GlassCard } from "@/src/components/GlassCard";
import { Button } from "@/src/components/ui/button";

export const FAQPage: React.FC = () => {
  const { t } = useTranslation("common");
  const [activeCategory, setActiveCategory] = useState("general");
  const [openQuestions, setOpenQuestions] = useState<string[]>([]);

  const toggleQuestion = (id: string) => {
    setOpenQuestions((prev) =>
      prev.includes(id) ? prev.filter((q) => q !== id) : [...prev, id],
    );
  };

  const categories = [
    { id: "general", label: t("faq.categories.general"), icon: HelpCircle },
    { id: "design", label: t("faq.categories.design"), icon: Sparkles },
    { id: "ordering", label: t("faq.categories.ordering"), icon: Package },
    { id: "shipping", label: t("faq.categories.shipping"), icon: Truck },
    { id: "payment", label: t("faq.categories.payment"), icon: CreditCard },
    {
      id: "sustainability",
      label: t("faq.categories.sustainability"),
      icon: Globe,
    },
  ];

  const faqs = {
    general: [
      {
        id: "g1",
        question: "What is Walnut Packaging Factory?",
        answer:
          "Walnut Packaging Factory is a premium die-cut packaging manufacturer specializing in custom packaging solutions. We offer an innovative online design studio where you can create, preview in 2D/3D/AR, and order custom packaging—all from your browser.",
      },
      {
        id: "g2",
        question: "What types of packaging do you offer?",
        answer:
          "We offer a wide range of packaging solutions including custom printed boxes, corrugated packaging, food-grade containers, retail packaging, industrial bulk packaging, and specialty die-cut designs. Browse our Products page to see the full range.",
      },
      {
        id: "g3",
        question: "Do you serve businesses of all sizes?",
        answer:
          "Yes! We work with everyone from startups and small businesses to large corporations. Our minimum order quantities are flexible, and we can scale to meet your needs whether you need 500 units or 500,000.",
      },
      {
        id: "g4",
        question: "What industries do you serve?",
        answer:
          "We serve a diverse range of industries including food & beverage, cosmetics & beauty, e-commerce, pharmaceuticals, electronics, retail, and more. Check our Industries page for detailed information.",
      },
      {
        id: "g5",
        question: "Where are you located?",
        answer:
          "Our manufacturing facility is located in the United States. We ship nationwide and internationally. Contact us for specific shipping options to your location.",
      },
    ],
    design: [
      {
        id: "d1",
        question: "How does the die-cut design studio work?",
        answer:
          "Our online design studio allows you to choose from 500+ die-cut templates, customize colors and materials, upload your artwork, and preview your design in 2D, 3D, and even AR (augmented reality). You can save designs to your account and request quotes directly from the studio.",
      },
      {
        id: "d2",
        question: "Can I upload my own design?",
        answer:
          "Absolutely! You can upload your own artwork, logos, and graphics. We accept AI, EPS, PDF, PNG, and JPG file formats. Our design team will review your files and work with you to ensure they're print-ready.",
      },
      {
        id: "d3",
        question: "Do you offer design assistance?",
        answer:
          "Yes! Our experienced design team is available to help bring your vision to life. Whether you need minor adjustments or complete design services, we're here to help. Contact us to discuss your project.",
      },
      {
        id: "d4",
        question: "What is the 3D/AR preview feature?",
        answer:
          "Our 3D preview lets you rotate and view your packaging from all angles before ordering. The AR (Augmented Reality) feature allows you to see your packaging in real-world scale using your smartphone or tablet camera—it's like holding the actual box!",
      },
      {
        id: "d5",
        question: "Can I save multiple design versions?",
        answer:
          "Yes! When you create an account, you can save unlimited design variations and compare them side-by-side. This is perfect for A/B testing different designs or maintaining a library of seasonal packaging.",
      },
    ],
    ordering: [
      {
        id: "o1",
        question: "What is the minimum order quantity?",
        answer:
          "Minimum order quantities vary by product type. For most standard packaging, the minimum is 500 units. However, we can accommodate smaller quantities for specialty items. Contact us for specific MOQ requirements.",
      },
      {
        id: "o2",
        question: "How do I request a quote?",
        answer:
          "You can request a quote in three ways: (1) Use our online quote form, (2) Save a design in our design studio and request a quote directly, or (3) Contact our sales team directly. We typically respond within 24 hours.",
      },
      {
        id: "o3",
        question: "How long does production take?",
        answer:
          "Standard production time is 10-15 business days after design approval. Rush production (5-7 business days) is available for an additional fee. Complex custom projects may require additional time—we'll provide accurate timelines with your quote.",
      },
      {
        id: "o4",
        question: "Will I see a proof before production?",
        answer:
          "Yes! We provide a digital proof for your approval before beginning production. You'll have the opportunity to review colors, placement, and all design elements. Production begins only after you approve the final proof.",
      },
      {
        id: "o5",
        question: "Can I reorder the same design?",
        answer:
          "Definitely! All your approved designs are saved in your account for easy reordering. You can reorder the exact same specifications or make modifications as needed.",
      },
      {
        id: "o6",
        question: "Do you offer samples?",
        answer:
          "Yes, we can provide physical samples of your design before full production. Sample costs and production time vary based on complexity. Contact us to discuss sample options for your project.",
      },
    ],
    shipping: [
      {
        id: "s1",
        question: "Where do you ship?",
        answer:
          "We ship nationwide across the United States and to select international locations. Shipping costs and delivery times vary by location and order size. Contact us for international shipping quotes.",
      },
      {
        id: "s2",
        question: "How much does shipping cost?",
        answer:
          "Shipping costs depend on order size, weight, destination, and delivery speed. We'll provide exact shipping costs with your quote. For large orders, we may offer free or discounted shipping.",
      },
      {
        id: "s3",
        question: "Do you offer expedited shipping?",
        answer:
          "Yes! We offer expedited shipping options including 2-day and overnight delivery (subject to availability). Expedited shipping costs will be calculated based on your order specifics.",
      },
      {
        id: "s4",
        question: "How can I track my order?",
        answer:
          "Once your order ships, you'll receive a tracking number via email. You can also view order status and tracking information in your account dashboard. We primarily ship via FedEx, UPS, and freight carriers for large orders.",
      },
      {
        id: "s5",
        question: "What if my order arrives damaged?",
        answer:
          "We take great care in packaging, but if your order arrives damaged, please contact us within 7 days with photos. We'll arrange for replacement or refund according to our quality guarantee policy.",
      },
    ],
    payment: [
      {
        id: "p1",
        question: "What payment methods do you accept?",
        answer:
          "We accept major credit cards (Visa, MasterCard, American Express, Discover), ACH bank transfers, and wire transfers. For established business customers, we also offer net-30 payment terms subject to credit approval.",
      },
      {
        id: "p2",
        question: "When is payment due?",
        answer:
          "For new customers, payment is due before production begins. Established customers with approved credit terms may pay within their specified payment window (typically net-30 days).",
      },
      {
        id: "p3",
        question: "Do you offer payment plans?",
        answer:
          "For large orders over $10,000, we can arrange payment plans on a case-by-case basis. Contact our sales team to discuss financing options for your project.",
      },
      {
        id: "p4",
        question: "Are there any hidden fees?",
        answer:
          "No hidden fees! Our quotes include all production costs. The only additional costs would be optional services like rush production, design services, or expedited shipping—which are clearly outlined before you commit.",
      },
      {
        id: "p5",
        question: "Can I get a refund if I cancel my order?",
        answer:
          "Cancellation policies vary depending on production stage. Orders can be cancelled with full refund before design approval. After production begins, cancellation may incur fees to cover materials and labor. Contact us immediately if you need to cancel.",
      },
    ],
    sustainability: [
      {
        id: "su1",
        question: "Are your materials eco-friendly?",
        answer:
          "Yes! We offer a wide range of sustainable materials including recycled cardboard, biodegradable plastics, FSC-certified paper, and compostable options. All our materials meet or exceed industry sustainability standards.",
      },
      {
        id: "su2",
        question: "Do you have sustainability certifications?",
        answer:
          "We are FSC (Forest Stewardship Council) certified and comply with all EPA regulations. Many of our materials carry additional certifications like SFI, biodegradable certifications, and FDA approval for food-grade packaging.",
      },
      {
        id: "su3",
        question: "Can packaging be recycled?",
        answer:
          "Most of our packaging is 100% recyclable. We clearly mark recycling instructions on all products and can help you choose materials that align with your sustainability goals and local recycling programs.",
      },
      {
        id: "su4",
        question: "What is your carbon footprint?",
        answer:
          "We're committed to reducing our environmental impact. Our facility uses energy-efficient equipment, we minimize waste through optimized production, and we offer carbon-neutral shipping options. View our Sustainability page for detailed information.",
      },
      {
        id: "su5",
        question: "Do you offer plant-based inks?",
        answer:
          "Yes! We use soy-based and vegetable-based inks as our standard printing option. These inks are renewable, produce less VOC emissions, and are easier to remove during recycling compared to petroleum-based inks.",
      },
    ],
  };

  return (
    <div className="min-h-screen pt-32 pb-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          <Link
            href="/"
            className="inline-flex items-center gap-2 text-walnut-700 dark:text-walnut-300 hover:text-walnut-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">{t("marketingPages.faq.back")}</span>
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-walnut-500 to-walnut-700 mb-6">
            <HelpCircle className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-walnut-900 dark:text-white mb-4">
            {t("marketingPages.faq.title")}
          </h1>
          <p className="text-lg text-walnut-700 dark:text-walnut-300">
            {t("marketingPages.faq.subtitle")}
          </p>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <Button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                variant={activeCategory === category.id ? "default" : "glass"}
                pill
                className={
                  activeCategory === category.id
                    ? "shadow-lg shadow-walnut-500/30"
                    : "text-walnut-900 dark:text-white"
                }
              >
                <category.icon className="w-5 h-5" />
                <span className="font-medium">{category.label}</span>
              </Button>
            ))}
          </div>
        </motion.div>

        {/* FAQ Items */}
        <div className="max-w-4xl mx-auto space-y-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {faqs[activeCategory as keyof typeof faqs].map((faq, index) => (
                <motion.div
                  key={faq.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * index }}
                >
                  <GlassCard
                    className="overflow-hidden cursor-pointer hover:scale-[1.01] transition-all duration-300"
                    onClick={() => toggleQuestion(faq.id)}
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <h3 className="text-lg font-semibold text-walnut-900 dark:text-white flex-1">
                          {faq.question}
                        </h3>
                        <motion.div
                          animate={{
                            rotate: openQuestions.includes(faq.id) ? 180 : 0,
                          }}
                          transition={{ duration: 0.3 }}
                          className="flex-shrink-0"
                        >
                          <ChevronDown className="w-6 h-6 text-walnut-600 dark:text-walnut-400" />
                        </motion.div>
                      </div>

                      <AnimatePresence>
                        {openQuestions.includes(faq.id) && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            <div className="pt-4 text-walnut-700 dark:text-walnut-300 leading-relaxed">
                              {faq.answer}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </GlassCard>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Still Have Questions CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-16 max-w-3xl mx-auto"
        >
          <GlassCard className="p-8 text-center">
            <MessageCircle className="w-12 h-12 text-walnut-600 dark:text-walnut-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-walnut-900 dark:text-white mb-3">
              {t("marketingPages.faq.stillQuestionsTitle")}
            </h3>
            <p className="text-walnut-700 dark:text-walnut-300 mb-6">
              {t("marketingPages.faq.stillQuestionsSubtitle")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-[33px] bg-gradient-to-r from-walnut-600 to-walnut-700 dark:from-walnut-500 dark:to-walnut-600 text-white font-semibold hover:shadow-lg hover:shadow-walnut-500/30 transition-all duration-300 hover:scale-105"
              >
                {t("marketingPages.faq.contactSupport")}
              </Link>
              <Link
                href="/quote"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-[33px] backdrop-blur-xl bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/10 text-walnut-900 dark:text-white font-semibold hover:bg-white/30 dark:hover:bg-black/30 transition-all duration-300 hover:scale-105"
              >
                Get a Quote
              </Link>
            </div>
          </GlassCard>
        </motion.div>
      </div>
    </div>
  );
};
